import { useState } from 'react';
import { Alert, Button, Card, CircularProgress, FormControl, FormHelperText, FormLabel, Input, Stack, Typography } from '@mui/joy';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../lib/api';

type Thresholds = { minimum_wind_mph: number; minimum_hail_inches: number };
const endpoint = '/api/integrations/reports/settings/';

function ThresholdForm({ initial, onSave, saving }: {
  initial: Thresholds; onSave: (values: Thresholds) => void; saving: boolean;
}) {
  const [wind, setWind] = useState(String(initial.minimum_wind_mph));
  const [hail, setHail] = useState(String(initial.minimum_hail_inches));
  return <Card component="form" onSubmit={(event) => {
    event.preventDefault();
    onSave({ minimum_wind_mph: Number(wind), minimum_hail_inches: Number(hail) });
  }} sx={{ maxWidth: 720, gap: 3 }}>
    <Typography level="title-lg">Events to include</Typography>
    <Typography level="body-md">Include wind events at or above your wind minimum, and hail events at or above your hail minimum. An event only needs to meet its own threshold.</Typography>
    <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
      <FormControl sx={{ flex: 1 }}>
        <FormLabel>Minimum wind speed</FormLabel>
        <Input type="number" value={wind} onChange={(event) => setWind(event.target.value)} endDecorator="mph" disabled={saving} slotProps={{ input: { min: 1, max: 300, step: 0.01, required: true } }} />
        <FormHelperText>1–300 mph</FormHelperText>
      </FormControl>
      <FormControl sx={{ flex: 1 }}>
        <FormLabel>Minimum hail diameter</FormLabel>
        <Input type="number" value={hail} onChange={(event) => setHail(event.target.value)} endDecorator="inches" disabled={saving} slotProps={{ input: { min: 0.01, max: 10, step: 0.01, required: true } }} />
        <FormHelperText>0.01–10 inches</FormHelperText>
      </FormControl>
    </Stack>
    <Typography level="body-sm">For example, 50 mph and 0.75 inches includes a 55 mph wind event or a 0.80-inch hail event. Other weather event types keep their existing inclusion rules.</Typography>
    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
      <Button type="submit" loading={saving}>Save weather report settings</Button>
      <Button variant="plain" disabled={saving} onClick={() => { setWind('60'); setHail('1'); }}>Use defaults: 60 mph / 1 inch</Button>
    </Stack>
  </Card>;
}

export function WeatherReportSettingsPage() {
  const { user } = useAuth();
  const allowed = Boolean(user?.is_organization_admin || user?.is_superuser);
  const queryClient = useQueryClient();
  const key = ['weather-report-settings', user?.id, user?.organization?.id];
  const settings = useQuery({ queryKey: key, enabled: allowed, refetchOnWindowFocus: false,
    queryFn: async () => (await apiClient.get<Thresholds>(endpoint)).data });
  const save = useMutation({ mutationFn: async (values: Thresholds) => (await apiClient.patch<Thresholds>(endpoint, values)).data,
    onSuccess: (values) => queryClient.setQueryData(key, values) });
  if (!allowed) return <Alert color="warning">Organization admins manage weather report settings.</Alert>;
  return <Stack gap={3}>
    <div><Typography level="h2">Weather reports</Typography>
      <Typography sx={{ mt: 1 }}>Choose the wind and hail minimums for your company’s new address and area reports, on web and mobile.</Typography></div>
    <Alert variant="soft">Changes apply to newly requested reports. Generate a new report to use updated thresholds; existing PDFs keep their original results. Storm Tracker alert settings are managed separately.</Alert>
    {settings.isPending ? <CircularProgress aria-label="Loading weather report settings" /> : settings.isError ?
      <Alert color="danger" endDecorator={<Button onClick={() => settings.refetch()}>Retry</Button>}>Unable to load weather report settings.</Alert> : settings.data ?
      <ThresholdForm key={`${key.join(':')}:${settings.data.minimum_wind_mph}:${settings.data.minimum_hail_inches}`} initial={settings.data} saving={save.isPending} onSave={(values) => save.mutate(values)} /> : null}
    {save.isError ? <Alert color="danger">Unable to save weather report settings. Please try again.</Alert> : null}
    {save.isSuccess ? <Alert color="success">Weather report settings saved. New reports will use these minimums.</Alert> : null}
  </Stack>;
}
